<template>
    <v-container fluid>
        <v-card class="ma-2" outlined>
            <v-card-title>
                <v-btn icon @click="goToDashboard">
                    <v-icon>mdi-chevron-left</v-icon>
                </v-btn>
                Rewards
                <v-spacer></v-spacer>
                <v-text-field v-model="search" append-icon="mdi-magnify" label="Search" single-line hide-details></v-text-field>
                <v-btn class="mr-2" color="primary" @click="goToHistory">
                    History
                </v-btn>
                <v-btn color="primary" @click="openAddDialog">
                    + Add
                </v-btn>
            </v-card-title>

            <!-- Reward List -->
            <v-data-table
                :headers="headers"
                :items="rewards"
                :loading="loading"
                :search="search"
                class="elevation-1"
            >
                <template #[`item.image`]="{ item }">
                    <v-img
                        :src="item.image"
                        max-width="100"
                        max-height="100"
                        contain
                    ></v-img>
                </template>
                <template #[`item.actions`]="{ item }">
                    <v-icon class="mr-2" @click="editItem(item)">
                        mdi-pencil
                    </v-icon>
                    <v-icon class="mr-2" @click="viewRedemptions(item)">
                        mdi-history
                    </v-icon>
                    <v-icon @click="removeItem(item)">
                        mdi-delete
                    </v-icon>
                </template>
            </v-data-table>

            <!-- Add/Edit Dialog -->
            <v-dialog v-model="dialog" max-width="800px">
                <v-card>
                    <v-card-title>
                        <span class="text-h5">{{ formTitle }}</span>
                    </v-card-title>

                    <v-card-text>
                        <v-container>
                            <v-form ref="form" lazy-validation v-model="valid">
                            <v-row>
                                <v-col cols="12">
                                    <v-text-field
                                        v-model="editedItem.name"
                                        label="Reward Name"
                                        :rules="requiredRule"
                                    ></v-text-field>
                                </v-col>
                                <v-col cols="12">
                                    <v-textarea
                                        v-model="editedItem.description"
                                        label="Description"
                                    ></v-textarea>
                                </v-col>
                                <v-col cols="12">
                                    <v-text-field
                                        v-model="editedItem.shortDescription"
                                        label="Short Description"
                                    ></v-text-field>
                                </v-col>
                                <v-col cols="12">
                                    <v-text-field
                                        label="Redeemed Points"
                                        type="number"
                                        v-model.number="editedItem.redeemedPoints"
                                        :rules="numberRule"
                                    ></v-text-field>
                                </v-col>
                                <v-col cols="12">
                                    <v-text-field
                                        label="Total Quantity"
                                        type="number"
                                        v-model.number="editedItem.totalQuantity"
                                        :rules="numberRule"
                                    ></v-text-field>
                                </v-col>
                                <v-col cols="12">
                                    <v-checkbox
                                        v-model="editedItem.isCoupon"
                                        label="Is this a coupon reward?"
                                        hide-details
                                    ></v-checkbox>
                                </v-col>
                                <v-col cols="12" v-if="editedItem.isCoupon">
                                    <v-textarea
                                        v-model="editedItem.couponCodesText"
                                        label="Coupon Codes (one per line, or comma separated)"
                                        :rules="[v => !!v || 'Please enter at least one coupon code']"
                                        @input="updateCouponQuantity"
                                    ></v-textarea>
                                    <v-alert type="info" dense text>
                                        Total Quantity will default to the number of entered codes but can be modified manually if needed.
                                    </v-alert>
                                </v-col>
                                <v-col cols="12">
                                    <v-file-input
                                        v-model="imageFile"
                                        label="Reward Image"
                                        accept=".jpg,.jpeg,.png"
                                        :rules="imageRule"
                                        @change="handleImageUpload"
                                    ></v-file-input>
                                </v-col>
                                <v-col cols="12" v-if="editedItem.image">
                                    <v-img
                                        :src="editedItem.image"
                                        max-width="200"
                                        max-height="200"
                                        contain
                                    ></v-img>
                                </v-col>
                                </v-row>
                            </v-form>
                        </v-container>
                    </v-card-text>

                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn color="blue darken-1" text @click="close">Cancel</v-btn>
                        <v-btn color="blue darken-1" text @click="save">Save</v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>

            <!-- Redemption History Dialog -->
            <v-dialog v-model="redemptionDialog" max-width="800px">
                <v-card>
                    <v-card-title>
                        <span class="text-h5">Redemption History - {{ selectedReward.name }}</span>
                    </v-card-title>

                    <v-card-title>
                        <v-spacer></v-spacer>
                        <v-text-field
                            v-model="searchRedemptions"
                            append-icon="mdi-magnify"
                            label="Search"
                            single-line
                            hide-details
                        ></v-text-field>
                    </v-card-title>
                    <v-data-table
                        :headers="computedRedemptionHeaders"
                        :items="redemptions"
                        :loading="loadingRedemptions"
                        :sort-by="['redeemedDate']"
                        :sort-desc="['desc']"
                        class="elevation-1"
                        :search="searchRedemptions"
                        :items-per-page="20"
                        :footer-props="{
                            'items-per-page-options': [10, 20, 50, 100]
                        }"
                    >
                        <template #[`item.image`]="{ item }">
                            <v-img
                                :src="item.image"
                                max-width="100"
                                max-height="100"
                                contain
                            ></v-img>
                        </template>
                        <template #[`item.createdDate`]="{ item }">
                            {{ formatDate(item.createdDate) }}
                        </template>
                        <template #[`item.status`]="{ item }">
                            <v-select
                                v-model="item.status"
                                :items="['pending', 'paid', 'sent']"
                                dense
                                outlined
                                hide-details
                                @change="updateStatus(item)"
                                :color="getStatusColor(item.status)"
                                v-if="item.status === 'pending' || item.status === 'paid'"
                            >
                                <template v-slot:selection="{ item }">
                                    <v-chip
                                        :color="getStatusColor(item)"
                                        text-color="white"
                                    >
                                        {{ item }}
                                    </v-chip>
                                </template>
                            </v-select>
                            <span v-else>
                                <v-chip
                                    :color="getStatusColor(item.status)"
                                    text-color="white"
                                >
                                    {{ item.status }}
                                </v-chip>
                            </span>
                        </template>
                        <template #[`item.actions`]="{ item }">
                            <v-icon @click="removeRedemption(item)" v-if="item.status === 'pending' || item.status === 'paid'">
                                mdi-delete
                            </v-icon>
                        </template>
                    </v-data-table>

                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn color="success" text @click="exportToExcel">
                            <v-icon left>mdi-file-excel</v-icon>
                            EXPORT
                        </v-btn>
                        <v-btn color="blue darken-1" text @click="redemptionDialog = false">Close</v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>

            <!-- Delete Confirmation Dialog -->
            <v-dialog v-model="deleteDialog" max-width="400px">
                <v-card>
                    <v-card-title class="text-h5">
                        Are you sure you want to delete the reward ?
                    </v-card-title>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn color="grey darken-1" text @click="deleteDialog = false">
                            Cancel
                        </v-btn>
                        <v-btn text @click="confirmDeleteReward">
                            Delete
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>

            <!-- Delete Redemption Confirmation Dialog -->
            <v-dialog v-model="deleteRedemptionDialog" max-width="400px">
                <v-card>
                    <v-card-title class="text-h5">
                        Are you sure you want to delete the redemption?
                    </v-card-title>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn color="grey darken-1" text @click="deleteRedemptionDialog = false">
                            Cancel
                        </v-btn>
                        <v-btn text @click="confirmDeleteRedemption">
                            Delete
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>

            <!-- Tracking Information Dialog -->
            <v-dialog v-model="trackingDialog" max-width="500px">
                <v-card>
                    <v-card-title>Add Tracking Information</v-card-title>
                    <v-card-text>
                        <v-form v-model="trackingFormValid">
                            <v-select
                                v-model="trackingForm.courierName"
                                :items="courierOptions"
                                label="Courier"
                                :rules="[trackingFormRules.required]"
                                required
                            ></v-select>
                            <v-text-field
                                v-model="trackingForm.trackingNumber"
                                label="Tracking Number"
                                :rules="[trackingFormRules.required]"
                                required
                            ></v-text-field>
                        </v-form>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn text @click="cancelTracking">Cancel</v-btn>
                        <v-btn color="success" text :disabled="!trackingForm.courierName || !trackingForm.trackingNumber" @click="submitTracking">Save</v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </v-card>
    </v-container>
</template>

<script>
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import { mapGetters } from "vuex";
import getWellnessRewards from "../data/getWellnessRewards";
import setWellnessReward from "../data/setWellnessReward";
import getRewardRedemptions from "../data/getRewardRedemptions";
import updateRedemptionStatus from "../data/updateRedemptionStatus";
import formatData from "../utils/formatData";
import removeRewards from "../data/removeRewards";
import removeRewardsHistory from "../data/removeRewardsHistory";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

export default {
    name: 'WellnessRewardList',
    metaInfo: {
        title: 'Rewards'
    },
    data() {
        return {
            search: "",
            searchRedemptions: "",
            loading: false,
            loadingRedemptions: false,
            dialog: false,
            redemptionDialog: false,
            deleteDialog: false,
            itemToDelete: {},
            deleteRedemptionDialog: false,
            redemptionToDelete: {},
            trackingDialog: false,
            trackingForm: {
                courierName: '',
                trackingNumber: ''
            },
            trackingFormValid: false,
            trackingFormRules: {
                required: v => !!v || 'Required'
            },
            courierOptions: [
                'ไปรษณีย์ไทย',
                'Kerry Express',
                'Flash Express',
                'J&T Express',
                'SCG Express',
                'DHL',
                'Ninja Van',
                'Best Express'
            ],
            itemToUpdate: null,
            headers: [
                { value: 'image', sortable: false },
                { text: 'Name', value: 'name' },
                { text: 'Redeemed Points', value: 'redeemedPoints' },
                { text: 'Total Quantity', value: 'totalQuantity' },
                { text: 'Remaining Quantity', value: 'remainingCount' },
                { text: 'Redeemed Count', value: 'redeemCount' },
                { text: 'Actions', value: 'actions', sortable: false }
            ],

            rewards: [],
            redemptions: [],
            editedIndex: -1,
            editedItem: {
                name: '',
                description: '',
                shortDescription: '',
                totalQuantity: '',
                redeemedPoints: '',
                isCoupon: false,
                couponCodesText: '',
                couponCodes: [],
                image: '',
                createdDate: null,
                createdBy: '',
                updatedDate: null,
                updatedBy: ''
            },
            defaultItem: {
                name: '',
                description: '',
                shortDescription: '',
                totalQuantity: '',
                redeemedPoints: '',
                isCoupon: false,
                couponCodesText: '',
                couponCodes: [],
                image: '',
                createdDate: null,
                createdBy: '',
                updatedDate: null,
                updatedBy: ''
            },
            imageFile: null,
            selectedReward: {},
            requiredRule: [v => !!v || 'This field is required'],
            imageRule: [
                v => !v || v.size < 2000000 || 'Image size should be less than 2 MB!',
                v => !v || ['image/png', 'image/jpg', 'image/jpeg'].includes(v.type) || 'Image must be in PNG, JPG, or JPEG format'
            ],
            numberRule: [v => !v || v > 0 || 'Number must be greater than 0'],
            valid: false
        };
    },

    computed: {
        ...mapGetters([
            "loggedInUserId",
            "isAuthenticated",
            "isSuperAdmin"
        ]),
        formTitle() {
            return this.editedIndex === -1 ? 'New Reward' : 'Edit Reward';
        },
        computedRedemptionHeaders() {
            if (this.selectedReward && this.selectedReward.isCoupon) {
                return [
                    { text: 'Redeem Date', value: 'redeemedDate' },
                    { text: 'Name', value: 'name' },
                    { text: 'Coupon Code', value: 'couponCode' },
                    { text: 'Status', value: 'status', width: '100px' }
                ];
            } else {
                return [
                    { text: 'Redeem Date', value: 'redeemedDate' },
                    { text: 'Name', value: 'name' },
                    { text: 'Phone Number', value: 'phoneNumber' },
                    { text: 'Address', value: 'address' },
                    { text: 'Status', value: 'status', width: '100px' },
                    { text: 'Actions', value: 'actions', sortable: false, width: '80px' }
                ];
            }
        }
    },

    created() {
        var vm = this;
        vm.$store.state.appTitle = "Thaisook Web Admin | Rewards";
        firebase.auth().onAuthStateChanged((user) => {
            if (user) {
                vm.$store.dispatch("autoSignIn", user);
                this.loading = true;
                setTimeout(() => {
                    vm.initialize();
                }, "1000");
            } else {
                console.log("no user");
            }
        });
    },

    methods: {
        async initialize() {
            if (this.isSuperAdmin) {
                (async () => {
                    this.loading = true;
                    this.rewards = await getWellnessRewards();
                    this.loading = false;
                })();
            } else {
                this.$router.push('/dashboard');
            }
        },

        updateCouponQuantity() {
            if (!this.editedItem.couponCodesText) {
                this.editedItem.totalQuantity = 0;
                return;
            }
            let codes = this.editedItem.couponCodesText.split(/[\n,]+/).map(c => c.trim()).filter(c => c);
            codes = [...new Set(codes)];
            this.editedItem.totalQuantity = codes.length;
        },

        editItem(item) {
            this.editedIndex = this.rewards.indexOf(item);
            this.editedItem = Object.assign({}, item);
            if (this.editedItem.isCoupon && this.editedItem.couponCodes) {
                this.editedItem.couponCodesText = this.editedItem.couponCodes.join('\n');
            } else {
                this.editedItem.isCoupon = false;
                this.editedItem.couponCodesText = '';
                this.editedItem.couponCodes = [];
            }
            this.dialog = true;
        },

        async viewRedemptions(item) {
            this.selectedReward = item;
            this.redemptionDialog = true;
            this.loadingRedemptions = true;
            try {
                this.redemptions = await getRewardRedemptions(item.id);
            } catch (error) {
                console.error('Error loading redemptions:', error);
            }
            this.loadingRedemptions = false;
        },

        removeItem(item) {
            this.itemToDelete = item;
            this.deleteDialog = true;
        },

        async confirmDeleteReward() {
            try {
                // Remove the reward from the list
                const index = this.rewards.indexOf(this.itemToDelete);
                if (index > -1) {
                    this.rewards.splice(index, 1);
                }
                await removeRewards(this.itemToDelete.id);
                this.deleteDialog = false;
                this.itemToDelete = {};
            } catch (error) {
                console.error('Error deleting reward:', error);
            }
        },

        async removeRedemption(item) {
            this.redemptionToDelete = item;
            this.deleteRedemptionDialog = true;
        },

        async confirmDeleteRedemption() {
            try {
                // Remove the redemption from the list
                const index = this.redemptions.indexOf(this.redemptionToDelete);
                if (index > -1) {
                    this.redemptions.splice(index, 1);
                }
                await removeRewardsHistory(this.selectedReward.id, this.redemptionToDelete.id);
                await this.initialize();
                this.deleteRedemptionDialog = false;
                this.redemptionToDelete = {};
            } catch (error) {
                console.error('Error deleting redemption:', error);
            }
        },

        openAddDialog() {
            this.editedIndex = -1;
            this.editedItem = Object.assign({}, this.defaultItem);
            this.dialog = true;
        },

        async handleImageUpload(file) {
            if (!file) return;
            
            try {
                const storageRef = firebase.storage().ref();
                const imageRef = storageRef.child(`wellness-rewards/${Date.now()}_${file.name}`);
                await imageRef.put(file);
                const imageUrl = await imageRef.getDownloadURL();
                this.editedItem.image = imageUrl;
            } catch (error) {
                console.error('Error uploading image:', error);
            }
        },

        async save() {
            (async () => {
                //to validate from before submit
                await this.$refs.form.validate();

                if (this.valid) {
                    (async () => {
                        let payload = Object.assign({}, this.editedItem);
                        if (payload.isCoupon) {
                            let codes = payload.couponCodesText
                                ? payload.couponCodesText.split(/[\n,]+/).map(c => c.trim()).filter(c => c)
                                : [];
                            codes = [...new Set(codes)];
                            payload.couponCodes = codes;
                            
                            // If user explicitly blanked it, or it genuinely needs default
                            if (!payload.totalQuantity && payload.totalQuantity !== 0) {
                                payload.totalQuantity = codes.length;
                            }
                        } else {
                            payload.couponCodes = [];
                        }
                        // We shouldn't store the temporary text field in DB
                        delete payload.couponCodesText;

                        if (this.editedIndex > -1) {
                            // Edit existing reward
                            payload.updatedDate = new Date();
                            payload.updatedBy = this.loggedInUserId;

                            payload.redeemedPoints = payload.redeemedPoints * 1;
                            payload.totalQuantity = payload.totalQuantity * 1;
                            await setWellnessReward(payload, payload.id);
                            Object.assign(this.rewards[this.editedIndex], payload);
                        } else {
                            // Add new reward
                            payload.createdDate = new Date();
                            payload.createdBy = this.loggedInUserId;
                            payload.updatedDate = new Date();
                            payload.updatedBy = this.loggedInUserId;

                            payload.redeemCount = 0;
                            payload.redeemedPoints = payload.redeemedPoints * 1;
                            payload.totalQuantity = payload.totalQuantity * 1;
                            const newId = await setWellnessReward(payload);
                            payload.id = newId;
                            this.rewards.push(payload);
                        }
                        this.close();
                        await this.initialize();
                    })();
                }
            })();
        },

        close() {
            this.dialog = false;
            this.$nextTick(() => {
                this.editedItem = Object.assign({}, this.defaultItem);
                this.editedIndex = -1;
                this.imageFile = null;
            });
        },

        formatDate(date) {
            return formatData('date', date);
        },

        goToDashboard() {
            this.$router.push('/dashboard');
        },

        goToHistory() {
            this.$router.push('/wellness-rewards/history');
        },

        async updateStatus(item) {
            if (item.status === 'sent') {
                if (this.selectedReward && this.selectedReward.isCoupon) {
                     try {
                         await updateRedemptionStatus(
                             item.rewardId, 
                             item.id, 
                             item.status, 
                             this.loggedInUserId,
                             '',
                             ''
                         );
                     } catch (e) { console.error('Error updating status for coupon:', e); }
                } else {
                    this.itemToUpdate = item;
                    this.trackingForm = {
                        courierName: '',
                        trackingNumber: ''
                    };
                    this.trackingDialog = true;
                }
            }
        },

        cancelTracking() {
            // Reset the status back to pending if user cancels
            if (this.itemToUpdate) {
                this.itemToUpdate.status = 'pending';
            }
            this.trackingDialog = false;
            this.itemToUpdate = null;
        },

        async submitTracking() {
            if (!this.trackingForm.courierName || !this.trackingForm.trackingNumber) return;
            
            try {
                // Update the item with tracking information
                if (this.itemToUpdate) {
                    this.itemToUpdate.courierName = this.trackingForm.courierName;
                    this.itemToUpdate.trackingNumber = this.trackingForm.trackingNumber;
                    
                    // Update the status with tracking information
                    await updateRedemptionStatus(
                        this.itemToUpdate.rewardId, 
                        this.itemToUpdate.id, 
                        this.itemToUpdate.status, 
                        this.loggedInUserId,
                        this.trackingForm.courierName,
                        this.trackingForm.trackingNumber
                    );
                }
                
                this.trackingDialog = false;
                this.itemToUpdate = null;
            } catch (error) {
                console.error('Error updating tracking information:', error);
            }
        },

        getStatusColor(status) {
            const colors = {
                'pending': 'orange',
                'paid': 'blue',
                'sent': 'green'
            };
            return colors[status] || 'grey';
        },

        exportToExcel() {
            if (!this.redemptions || this.redemptions.length === 0) {
                alert('No data to export');
                return;
            }

            const exportData = this.redemptions.map(r => {
                const baseData = {
                    'Redeem Date': this.formatDate(r.redeemedDate),
                    'Reward Name': r.rewardName || this.selectedReward.name,
                    'User Name': r.name,
                    'Points': r.redeemedPoints,
                    'Status': r.status || 'pending',
                };
                
                if (this.selectedReward && this.selectedReward.isCoupon) {
                    return {
                        ...baseData,
                        'Coupon Code': r.couponCode || '-'
                    };
                } else {
                    return {
                        ...baseData,
                        'Phone Number': r.phoneNumber || '-',
                        'Address': r.address || '-',
                        'Courier': r.courierName || '-',
                        'Tracking Number': r.trackingNumber || '-'
                    };
                }
            });

            const worksheet = XLSX.utils.json_to_sheet(exportData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Redemptions");

            const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
            const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
            
            const fileName = `Redemptions_${this.selectedReward.name}_${new Date().getTime()}.xlsx`;
            saveAs(data, fileName);
        }
    }
};
</script> 